/** Join a program with its code and your name (the fallback when a link email is blocked). A CEO/CRO seat PIN in the answer is shown once before moving on. */
import { useState, type FormEvent } from "react";
import type { ErrorBody } from "@bsim/contracts";
import type { RouteCall } from "../api/types";
import { landingPath } from "../shell/landing";
import { Link, navigate, useLocation } from "../shell/history";
import { GxErrorBanner } from "../theme/primitives";
import { GxSeatPinNotice } from "../theme/SeatPinNotice";
import { GxTextField } from "../theme/TextField";
import { AuthLayout } from "./AuthLayout";
import { authHref, SIGN_IN_PATH, safeReturnTo } from "./returnTo";

/** Codes are typed from a card: capitals, no spaces (the contract's alphabet has no 0, O, 1 or I). */
const tidyCode = (raw: string): string => raw.replace(/\s+/g, "").toUpperCase();

export function JoinPage({ join }: { readonly join: RouteCall<"authJoinCode"> }) {
  const params = new URLSearchParams(useLocation().search);
  const returnTo = safeReturnTo(params.get("returnTo"));
  const [joinCode, setJoinCode] = useState(tidyCode(params.get("code") ?? ""));
  const [displayName, setDisplayName] = useState("");
  const [error, setError] = useState<ErrorBody["error"] | null>(null);
  const [pending, setPending] = useState(false);
  const [pin, setPin] = useState<{ readonly pin: string; readonly role: "ceo" | "cro"; readonly next: string } | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setPending(true);
    setError(null);
    const result = await join(undefined, { joinCode, displayName: displayName.trim() });
    setPending(false);
    if (!result.ok) { setError(result.error); return; }
    // An unassigned joiner (teamId null) lands on the same page as everyone: P1 shows "Your facilitator hasn't placed you on a team yet." and waits for `membership.changed`.
    const next = returnTo ?? landingPath(result.value.principal) ?? "/";
    const seat = result.value.principal.memberships[0]?.teamRole;
    if (result.value.seatPin === undefined) navigate(next, { replace: true });
    else setPin({ pin: result.value.seatPin, role: seat === "cro" ? "cro" : "ceo", next });
  };

  return (
    <AuthLayout>
      <div className="gx-eyebrow">Join</div>
      <h1 className="gx-h1 gx-auth-h">Join with a code</h1>
      <p className="gx-auth-lead">Your facilitator has a join code for the program. Enter it with your name as your teammates should see it.</p>
      <form className="gx-auth-form" onSubmit={(e) => { void submit(e); }} data-testid="auth.joinForm">
        {error === null ? null : <GxErrorBanner error={error} />}
        <GxTextField id="auth-code" label="Join code" name="joinCode" autoComplete="off" autoCapitalize="characters" spellCheck={false} required hint={joinCode.length > 0 && joinCode.length < 6 ? "That code is too short. Enter the whole code your facilitator gave you." : undefined} autoFocus={joinCode === ""} minLength={6} maxLength={10} value={joinCode} invalid={error?.code === "JOIN_CODE_INVALID"} onChange={(e) => setJoinCode(tidyCode(e.target.value))} />
        <GxTextField id="auth-name" label="Your name" name="displayName" autoComplete="name" required minLength={2} maxLength={80} autoFocus={joinCode !== ""} value={displayName} invalid={error?.code === "VALIDATION"} onChange={(e) => setDisplayName(e.target.value)} />
        <button type="submit" className="gx-btn gx-btn--primary gx-btn--lg gx-auth-submit" disabled={pending || joinCode.length < 6 || displayName.trim().length < 2}>{pending ? "Joining…" : "Join the program"}</button>
      </form>
      <div className="gx-auth-or"><span>Invited by email?</span></div>
      <div className="gx-auth-acts"><Link href={authHref(SIGN_IN_PATH, returnTo)} className="gx-btn gx-btn--secondary" data-testid="auth.signInLink">Get a sign-in link</Link></div>
      {pin === null ? null : <GxSeatPinNotice pin={pin.pin} role={pin.role} onDone={() => { setPin(null); navigate(pin.next, { replace: true }); }} />}
    </AuthLayout>
  );
}
